import classNames from "classnames";
import { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { BaseOption } from "entities/common";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";
import { ReactComponent as CloseSVG } from "assets/images/common/close-round.svg";

import useDebounce from "hooks/useDebounce";
import { VALIDATION_CLASS } from "utils/constants";

type PropTypes = {
  label?: string;
  error?: string;
  hint?: string;
  variant?: "primary" | "secondary";
  isRequired?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: string;
  selectedLabel?: string;
  onChange: (value: string, label: string, option?: BaseOption) => void;
  onSearch: (search: string, page: number) => Promise<{ options: BaseOption[]; hasMore: boolean }>;
  className?: string;
};

const AsyncSearchableSelect: FC<PropTypes> = ({
  label,
  error,
  hint,
  variant = "primary",
  isRequired,
  isDisabled,
  isClearable,
  placeholder,
  searchPlaceholder,
  value,
  selectedLabel,
  onChange,
  onSearch,
  className,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<BaseOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [dropdownLayout, setDropdownLayout] = useState<{
    top: number;
    left: number;
    width: number;
    position: "fixed" | "absolute";
  } | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const displayLabel = useMemo(() => {
    if (selectedLabel) return selectedLabel;
    const found = options.find((opt) => opt.value === value);
    return found?.label || "";
  }, [selectedLabel, options, value]);

  // Fetch options when search changes (reset to page 1)
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setIsLoading(true);

    onSearch(debouncedSearch, 1).then((result) => {
      if (cancelled) return;
      setOptions(result.options);
      setHasMore(result.hasMore);
      setPage(1);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, isOpen, onSearch]);

  // Load options for a controlled value when the label is unknown (e.g. edit mode hydrate)
  useEffect(() => {
    if (!value || selectedLabel || isOpen) return;

    let cancelled = false;
    setIsLoading(true);

    onSearch("", 1).then((result) => {
      if (cancelled) return;
      setOptions(result.options);
      setHasMore(result.hasMore);
      setPage(1);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [value, selectedLabel, isOpen, onSearch]);

  // Load more on scroll
  const handleScroll = useCallback(() => {
    const el = dropdownRef.current;
    if (!el || isLoading || !hasMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (nearBottom) {
      const nextPage = page + 1;
      setIsLoading(true);

      onSearch(debouncedSearch, nextPage).then((result) => {
        setOptions((prev) => [...prev, ...result.options]);
        setHasMore(result.hasMore);
        setPage(nextPage);
        setIsLoading(false);
      });
    }
  }, [isLoading, hasMore, page, debouncedSearch, onSearch]);

  const updateDropdownPosition = useCallback(() => {
    if (!ref.current) return;
    const trigger = ref.current;
    const rect = trigger.getBoundingClientRect();
    const dialogEl = trigger.closest("dialog");

    // Native <dialog> top layer sits above normal stacking; portal into the dialog (see PhoneInput).
    if (dialogEl) {
      setPortalTarget(dialogEl);
      const dRect = dialogEl.getBoundingClientRect();
      setDropdownLayout({
        top: rect.bottom - dRect.top + 4,
        left: rect.left - dRect.left,
        width: rect.width,
        position: "absolute",
      });
    } else {
      setPortalTarget(document.body);
      setDropdownLayout({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        position: "fixed",
      });
    }
  }, []);

  const handleOpen = useCallback(() => {
    if (!isDisabled) {
      setOpen(true);
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isDisabled]);

  const handleSelect = useCallback(
    (option: BaseOption) => {
      onChange(option.value, option.label, option);
      setOpen(false);
      setSearch("");
    },
    [onChange],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
  }, []);

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onChange("", "", undefined);
    },
    [onChange],
  );

  const canClear = !!isClearable && !!value && !isDisabled && !isOpen;

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownLayout(null);
      setPortalTarget(null);
      return;
    }
    updateDropdownPosition();
    const raf = requestAnimationFrame(updateDropdownPosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  // Click outside (trigger + portaled dropdown)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setSearch("");
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={ref}
      className={classNames("select", className, `select--${variant}`, {
        "select--disabled": isDisabled,
      })}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames("select__field", {
          "select__field--filled": !!displayLabel || isOpen,
          "select__field--open": isOpen,
          "select__field--error": !!error,
          [VALIDATION_CLASS]: !!error,
        })}
        onClick={!isOpen ? handleOpen : undefined}
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={isDisabled ? -1 : 0}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            className="select__search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder || displayLabel || placeholder || "Search..."}
          />
        ) : (
          <span className="select__value">{displayLabel || placeholder || ""}</span>
        )}

        {!!label && (
          <span
            className={classNames("select__label", {
              "select__label--required": isRequired,
            })}
          >
            {label}
          </span>
        )}

        {canClear && (
          <button
            type="button"
            className="select__clear"
            onClick={handleClear}
            aria-label="Clear selection"
            tabIndex={-1}
          >
            <CloseSVG />
          </button>
        )}

        <span className={classNames("select__arrow", { "select__arrow--open": isOpen })}>
          <ArrowSVG />
        </span>
      </div>

      {isOpen &&
        dropdownLayout &&
        portalTarget &&
        createPortal(
          <ul
            className="select__dropdown"
            ref={dropdownRef}
            onScroll={handleScroll}
            style={{
              position: dropdownLayout.position,
              top: dropdownLayout.top,
              left: dropdownLayout.left,
              width: dropdownLayout.width,
              right: "auto",
              zIndex: dropdownLayout.position === "fixed" ? 10001 : 20,
            }}
          >
            {options.length > 0
              ? options.map((option) => (
                  <li
                    key={option.value}
                    className={classNames("select__option", {
                      "select__option--selected": option.value === value,
                      "select__option--disabled": option.disabled,
                    })}
                    onClick={() => !option.disabled && handleSelect(option)}
                  >
                    {option.label}
                  </li>
                ))
              : !isLoading && <li className="select__option select__option--disabled">No results found</li>}
            {isLoading && <li className="select__option select__option--disabled">Loading...</li>}
          </ul>,
          portalTarget,
        )}

      {!!error && <div className="select__error">{error}</div>}
      {!!hint && !error && <div className="select__hint">{hint}</div>}
    </div>
  );
};

export default memo(AsyncSearchableSelect);
